// <Tabs> — accessible tabs with role="tablist" / role="tab" / role="tabpanel".
//
// Per a11y-plan.md keyboard map: Arrow{Left,Right,Up,Down}, Home/End, Enter
// activate. We use the roving-tabindex hook so each tab participates in the
// platform's focus-management contract.
//
// Token usage: color.surface[2..3], color.outline, font.role.label,
// space[2..3], radius.sm, motion.duration.fast.

import { useId, type CSSProperties, type ReactNode } from 'react';
import { tokens } from '../tokens';
import { useRovingTabindex, useReducedMotion } from '../hooks';

export interface TabsTab {
  id: string;
  label: string;
  /** Disabled tabs are skipped during arrow nav. */
  disabled?: boolean;
}

export interface TabsProps {
  tabs: TabsTab[];
  value: string;
  onChange(next: string): void;
  /** Required aria-label for the tablist. */
  'aria-label': string;
  /** Children: rendered panels keyed by tab id. */
  children?: ReactNode;
  orientation?: 'horizontal' | 'vertical';
  className?: string;
  /**
   * Extra style merged onto the tablist row. Use `{ flexWrap: 'wrap' }` to keep
   * every tab visible without horizontal scroll in a narrow/resizable panel.
   */
  tablistStyle?: CSSProperties;
  /** Extra style merged onto the outer tabs root. */
  style?: CSSProperties;
  /** Extra style merged onto each tabpanel. */
  panelStyle?: CSSProperties;
  'data-testid'?: string;
}

export function Tabs({
  tabs,
  value,
  onChange,
  'aria-label': ariaLabel,
  children,
  orientation = 'horizontal',
  className,
  tablistStyle,
  style,
  panelStyle,
  'data-testid': testId,
}: TabsProps) {
  const baseId = useId();
  const activeIndex = Math.max(0, tabs.findIndex((t) => t.id === value));
  const roving = useRovingTabindex({
    count: tabs.length,
    orientation,
    initialIndex: activeIndex,
    onActivate: (idx) => {
      const tab = tabs[idx];
      if (tab && !tab.disabled) onChange(tab.id);
    },
  });
  const reduced = useReducedMotion();

  return (
    <div className={className} data-testid={testId} style={style}>
      <div
        role="tablist"
        aria-label={ariaLabel}
        aria-orientation={orientation}
        style={{
          display: 'flex',
          flexDirection: orientation === 'horizontal' ? 'row' : 'column',
          gap: tokens.space[1],
          borderBottom: orientation === 'horizontal'
            ? `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`
            : undefined,
          borderRight: orientation === 'vertical'
            ? `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`
            : undefined,
          ...tablistStyle,
        }}
      >
        {tabs.map((tab, idx) => {
          const selected = tab.id === value;
          const itemProps = roving.getItemProps(idx);
          return (
            <button
              key={tab.id}
              {...itemProps}
              role="tab"
              type="button"
              id={`${baseId}-tab-${tab.id}`}
              aria-controls={`${baseId}-panel-${tab.id}`}
              aria-selected={selected}
              aria-disabled={tab.disabled}
              disabled={tab.disabled}
              onClick={() => {
                if (!tab.disabled) onChange(tab.id);
              }}
              style={{
                appearance: 'none',
                background: 'transparent',
                border: 0,
                borderBottom: orientation === 'horizontal'
                  ? `2px solid ${selected ? tokens.color.accent.primary : 'transparent'}`
                  : undefined,
                borderRight: orientation === 'vertical'
                  ? `2px solid ${selected ? tokens.color.accent.primary : 'transparent'}`
                  : undefined,
                padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
                color: tab.disabled
                  ? tokens.color.ink.muted
                  : selected
                    ? tokens.color.ink.primary
                    : tokens.color.ink.secondary,
                fontFamily: tokens.font.family.ui,
                fontSize: tokens.font.role.body.size,
                fontWeight: selected ? tokens.font.weight.semibold : tokens.font.weight.regular,
                cursor: tab.disabled ? 'not-allowed' : 'pointer',
                minHeight: tokens.density.touchMin,
                transitionProperty: 'border-color, color',
                transitionDuration: `${reduced ? 0 : tokens.motion.duration.fast}ms`,
                transitionTimingFunction: tokens.motion.easing.standard,
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      {tabs.map((tab) => {
        const selected = tab.id === value;
        return (
          <div
            key={tab.id}
            role="tabpanel"
            id={`${baseId}-panel-${tab.id}`}
            aria-labelledby={`${baseId}-tab-${tab.id}`}
            hidden={!selected}
            tabIndex={0}
            style={{ outline: 'none', padding: tokens.space[3], ...panelStyle }}
          >
            {selected ? children : null}
          </div>
        );
      })}
    </div>
  );
}
